import { useState, useEffect, useMemo } from "react"
import { getKaptcherParcelle } from "@/lib/explore/api/kaptcher-data"
import type { IParcel } from "@/types/explore"
import type { FeatureCollection } from "geojson"

type ParcelRef = { prefix?: string; section: string; number: string }

export function useProjectParcels(parcels: ParcelRef[] | undefined, cityCode: string | undefined) {
  const [fetchedParcels, setFetchedParcels] = useState<IParcel[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!parcels?.length || !cityCode) return

    const idus = parcels.map((p) => {
      const prefix = (p.prefix || "000").padStart(3, "0")
      const section = p.section.padStart(2, "0")
      const number = p.number.padStart(4, "0")
      return `${cityCode}${prefix}${section}${number}`
    })

    let cancelled = false
    setLoading(true)
    Promise.all(idus.map((idu) => getKaptcherParcelle(idu).catch(() => null))).then((results) => {
      if (cancelled) return
      setFetchedParcels(results.filter((r): r is IParcel => r != null && r.geometry != null))
      setLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [parcels, cityCode])

  const parcelGeojson = useMemo<FeatureCollection | null>(() => {
    if (fetchedParcels.length === 0) return null
    return {
      type: "FeatureCollection",
      features: fetchedParcels.map((r) => ({
        type: "Feature" as const,
        geometry: r.geometry,
        properties: r.properties || {},
      })),
    }
  }, [fetchedParcels])

  return { fetchedParcels, parcelGeojson, loading }
}
